'use client';

import React, { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ArrowUpCircle, Printer, Search } from 'lucide-react';
import { Fab } from '@/ui/primitives/fab';

type HeadingItem = { text: string; id: string };

export default function LegalDocument({ content }: { content: string }) {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Extract headings when content changes - use useMemo instead of useEffect + setState
  const headings = useMemo(() => {
    const regex = /^## (.*$)/gm;
    const extractedHeadings = new Array<HeadingItem>();
    let match;

    while ((match = regex.exec(content)) !== null) {
      const text = match[1];
      if (!text) continue;
      extractedHeadings.push({
        text,
        id: text.toLowerCase().replace(/[^\w]+/g, '-'),
      });
    }

    return extractedHeadings;
  }, [content]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleSearch = () => {
    setIsSearching(!isSearching);
    if (!isSearching) {
      setTimeout(() => document.getElementById('search-input')?.focus(), 100);
    } else {
      setSearchTerm('');
      removeHighlights();
    }
  };

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    removeHighlights();

    const contentElement = contentRef.current;
    if (!contentElement) return;

    const text = contentElement.innerHTML;
    const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');

    if (regex.test(text)) {
      contentElement.innerHTML = text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');

      // Scroll to first result
      const firstMark = contentElement.querySelector('mark');
      if (firstMark) {
        firstMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const removeHighlights = () => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const marks = contentElement.querySelectorAll('mark');
    marks.forEach((mark) => {
      const text = document.createTextNode(mark.textContent || '');
      if (mark.parentNode) {
        mark.parentNode.replaceChild(text, mark);
      }
    });
  };

  const escapeRegExp = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        {/* Table of Contents Toggle */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex-grow max-w-md">
          <h3 className="text-lg font-semibold mb-2">Table of Contents</h3>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {headings.map((heading) => (
              <li key={heading.id}>
                <button
                  onClick={() => scrollToSection(heading.id)}
                  className="text-left hover:text-blue-600 w-full truncate"
                >
                  {heading.text}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={toggleSearch}
            className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors"
            aria-label="Search document"
          >
            <Search size={18} />
            <span>Search</span>
          </button>

          <button
            onClick={handlePrint}
            className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors print:hidden"
            aria-label="Print document"
          >
            <Printer size={18} />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {isSearching && (
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <input
              id="search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search document..."
              className="border border-gray-300 rounded-md px-3 py-2 flex-grow"
            />
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
            >
              Search
            </button>
          </div>
        </form>
      )}

      {/* Content */}
      <div ref={contentRef} className="print:mt-0">
        <article className="prose prose-lg max-w-none print:prose-sm">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h2: ({ node, ...props }) => {
                const id = (props.children ?? '')
                  .toString()
                  .toLowerCase()
                  .replace(/[^\w]+/g, '-');
                return <h2 id={id} {...props} />;
              },
            }}
          >
            {content}
          </ReactMarkdown>
        </article>
      </div>

      {/* Back to top button */}
      {showBackToTop && (
        <Fab
          onClick={scrollToTop}
          variant="default"
          size="default"
          position="bottom-right"
          className="print:hidden"
          aria-label="Back to top"
        >
          <ArrowUpCircle size={24} />
        </Fab>
      )}

      <style jsx global>{`
        @media print {
          body {
            font-size: 12pt;
          }
          .prose h1 {
            font-size: 18pt;
          }
          .prose h2 {
            font-size: 16pt;
          }
          .prose h3 {
            font-size: 14pt;
          }
          .prose p,
          .prose li {
            font-size: 10pt;
            line-height: 1.4;
          }
          @page {
            margin: 1.5cm;
          }
        }

        /* Add this to your global.css or as a style tag in your LegalDocument component */

        .prose h1 {
          font-size: 2rem;
          font-weight: 800;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #1a202c;
        }

        .prose h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #2d3748;
        }

        .prose h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          color: #4a5568;
        }

        /* Adjust paragraph spacing */
        .prose p {
          margin-bottom: 1rem;
        }

        /* Style for emphasized text in legal documents */
        .prose strong {
          font-weight: 700;
        }

        /* Add some styling for blockquotes which are common in legal docs */
        .prose blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          font-style: italic;
          margin: 1rem 0;
        }
      `}</style>
    </div>
  );
}
